import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { convertAdminRoleToFlag } from '@/vuex/modules/auth/auth.helpers';
import {
    bulkDeleteUsers,
    bulkUpdateUserPermissions,
    createUser,
    fetchUser,
    fetchUsers,
    updateUser,
    updateFeedbackVendorUserForUser,
    updateUserFlag,
    updateUserFeatureFlag,
    updateAdoptManagerFlagAsSuper,
} from '@/stores/users.utils';
import {
    buildBulkDeleteRequestId,
    getBulkDeleteStatus,
    pollBulkDeleteStatus,
} from '@/stores/users-bulk-delete-poll.utils';
import {
    buildBulkPermissionRequestId,
    getBulkPermissionStatus,
    pollBulkPermissionStatus,
} from '@/stores/users-bulk-permission-poll.utils';

import { useSubscriptionStore } from '@/stores/subscription.store';

interface UserRecord {
    id: string;
    username: string;
    applicationRoles?: unknown;
    [key: string]: unknown;
}

interface RequestResponse {
    requestId: string;
    conflict?: boolean;
}

interface BulkDeleteEntry {
    code: number;
    userId: string;
    [key: string]: unknown;
}

interface BulkPermissionEntry {
    code: number;
    user?: UserRecord;
    [key: string]: unknown;
}

interface BulkStatus<T> {
    status: string;
    error?: string;
    results?: T[];
}

interface FlagRequest {
    flagName: string;
    value: boolean;
}

interface FlagQuery {
    name: string;
}

interface UsersState {
    error: unknown;
    fetching: boolean;
    inviteStatus: string | null;
    map: Record<string, UserRecord>;
}

export const useUsersStore = defineStore('users', {
    state: (): UsersState => ({
        error: null,
        fetching: false,
        inviteStatus: null,
        map: {},
    }),
    getters: {
        list (state) {
            return values(state.map);
        },
        userById (state) {
            return (id: string) => state.map[id];
        },
        userByUsername (): (username: string) => UserRecord | undefined {
            return (username: string) => {
                return this.list.find((user) => user.username.toLowerCase().trim() === username.toLowerCase().trim());
            };
        },
    },
    actions: {
        setMap ({ map }: { map: Record<string, UserRecord> }) {
            this.map = map;
        },
        setMapAtKey ({ user }: { user: UserRecord }) {
            this.map[user.id] = user;
        },
        deleteMapAtKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setInviteStatus ({ inviteStatus }: { inviteStatus: string | null }) {
            this.inviteStatus = inviteStatus;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            this.setFetching({ fetching: true });

            try {
                const userList = await fetchUsers();
                this.setMap({ map: keyBy(userList as UserRecord[], 'id') });
            } catch (error) {
                this.setError({ error });
            }

            this.setFetching({ fetching: false });
        },
        async loadOne ({ reFetch = false, id, permissionSets = false }: { reFetch?: boolean; id: string; permissionSets?: boolean } = { id: '' }) {
            if (this.map[id] && !reFetch) {
                return;
            }

            this.setFetching({ fetching: true });
            try {
                const user = await fetchUser({ id, permissionSets });
                this.setFetching({ fetching: false });
                this.setMapAtKey({ user });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async invite (user: unknown) {
            try {
                const { status } = await createUser(user) as { status: number };
                if (status === 202) {
                    this.setInviteStatus({ inviteStatus: 'sent' });
                } else {
                    this.setInviteStatus({ inviteStatus: 'added' });
                }
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        async remove ({ id }: { id: string }) {
            this.deleteMapAtKey({ id });
        },
        async submitBulkRemove (userIds: string[]) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO APP-157986: replace when auth migrates to Pinia
            const subscriptionId = useSubscriptionStore().active.id;
            const userId = vuexStore.state.auth?.user?.id;

            const { requestId, conflict } = await bulkDeleteUsers(userIds) as RequestResponse;

            if (conflict) {
                return {
                    requestId: buildBulkDeleteRequestId({ subscriptionId, userId }),
                    conflict: true,
                };
            }

            return { requestId, conflict: false };
        },
        async pollAndCommitBulkRemove (payload: string | { requestId: string; signal?: AbortSignal }) {
            const { requestId, signal } = typeof payload === 'string'
                ? { requestId: payload, signal: undefined }
                : payload;

            const result = await pollBulkDeleteStatus({ requestId, signal }) as BulkStatus<BulkDeleteEntry>;

            if (result.status === 'failed') {
                throw new Error(result.error || 'Bulk delete failed');
            }

            const entries = result.results || [];
            const deleted: string[] = [];
            const failed: unknown[] = [];
            for (const entry of entries) {
                if (entry.code === 204) {
                    this.deleteMapAtKey({ id: entry.userId });
                    deleted.push(entry.userId);
                } else {
                    failed.push(entry);
                }
            }

            return { deleted, failed, total: entries.length };
        },
        async probeForActiveBulkRemove () {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO APP-157986: replace when auth migrates to Pinia
            const subscriptionId = useSubscriptionStore().active.id;
            const userId = vuexStore.state.auth?.user?.id;

            if (!subscriptionId || !userId) {
                return null;
            }

            const requestId = buildBulkDeleteRequestId({ subscriptionId, userId });
            const initial = await getBulkDeleteStatus(requestId) as BulkStatus<BulkDeleteEntry>;

            if (initial.status === 'none') {
                return null;
            }

            if (initial.status === 'in_progress') {
                return { requestId, terminal: false };
            }

            if (initial.status === 'failed') {
                return {
                    requestId,
                    terminal: true,
                    failedJob: true,
                    failed: [],
                    total: 0,
                    error: initial.error,
                };
            }

            const entries = initial.results || [];
            const failed: unknown[] = [];
            for (const entry of entries) {
                if (entry.code === 204) {
                    this.deleteMapAtKey({ id: entry.userId });
                } else {
                    failed.push(entry);
                }
            }

            return {
                requestId,
                terminal: true,
                failedJob: false,
                failed,
                total: entries.length,
            };
        },
        async submitBulkUpdatePermissions (updateRequests: unknown) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO APP-157986: replace when auth migrates to Pinia
            const subscriptionId = useSubscriptionStore().active.id;
            const userId = vuexStore.state.auth?.user?.id;

            const { requestId, conflict } = await bulkUpdateUserPermissions(updateRequests) as RequestResponse;

            if (conflict) {
                return {
                    requestId: buildBulkPermissionRequestId({ subscriptionId, userId }),
                    conflict: true,
                };
            }

            return { requestId, conflict: false };
        },
        async pollAndCommitBulkUpdatePermissions (payload: string | { requestId: string; signal?: AbortSignal }) {
            const { requestId, signal } = typeof payload === 'string'
                ? { requestId: payload, signal: undefined }
                : payload;

            const result = await pollBulkPermissionStatus({ requestId, signal }) as BulkStatus<BulkPermissionEntry>;

            if (result.status === 'failed') {
                throw new Error(result.error || 'Bulk permission update failed');
            }

            const entries = result.results || [];
            const succeeded: unknown[] = [];
            const failed: unknown[] = [];
            for (const entry of entries) {
                if (entry.code === 200 && entry.user) {
                    this.setMapAtKey({ user: entry.user });
                    succeeded.push(entry);
                } else {
                    failed.push(entry);
                }
            }

            return { succeeded, failed, total: entries.length };
        },
        async probeForActiveBulkUpdatePermissions () {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO APP-157986: replace when auth migrates to Pinia
            const subscriptionId = useSubscriptionStore().active.id;
            const userId = vuexStore.state.auth?.user?.id;

            if (!subscriptionId || !userId) {
                return null;
            }

            const requestId = buildBulkPermissionRequestId({ subscriptionId, userId });
            const initial = await getBulkPermissionStatus(requestId) as BulkStatus<BulkPermissionEntry>;

            if (initial.status === 'none') {
                return null;
            }

            if (initial.status === 'in_progress') {
                return { requestId, terminal: false };
            }

            if (initial.status === 'failed') {
                return {
                    requestId,
                    terminal: true,
                    failedJob: true,
                    succeeded: [],
                    failed: [],
                    total: 0,
                    error: initial.error,
                };
            }

            const entries = initial.results || [];
            const succeeded: unknown[] = [];
            const failed: unknown[] = [];
            for (const entry of entries) {
                if (entry.code === 200 && entry.user) {
                    this.setMapAtKey({ user: entry.user });
                    succeeded.push(entry);
                } else {
                    failed.push(entry);
                }
            }

            return {
                requestId,
                terminal: true,
                failedJob: false,
                succeeded,
                failed,
                total: entries.length,
            };
        },
        async update ({ user: userToUpdate }: { user: UserRecord }) {
            this.setFetching({ fetching: true });

            try {
                // sending applicationRoles indicates this is a v2 request
                // and we DO NOT want to set flags in that case
                const user = userToUpdate.applicationRoles ? userToUpdate : convertAdminRoleToFlag(userToUpdate);

                const updatedUser = await updateUser(user) as UserRecord;
                this.setFetching({ fetching: false });
                this.setMapAtKey({ user: updatedUser });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async updateFlag ({ queryParams, setValue, id, subscriptionId }: { queryParams: FlagQuery; setValue: unknown; id: string; subscriptionId?: string }) {
            const restrictedFlags = ['systemUser'];

            if (includes(restrictedFlags, queryParams.name)) {
                return;
            }

            this.setFetching({ fetching: true });
            try {
                // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                const vuexStore = require('@/vuex/store').default; // TODO APP-157986: replace when auth migrates to Pinia
                if (queryParams.name === 'adoptManager' && vuexStore.getters['auth/isSuper']) {
                    await updateAdoptManagerFlagAsSuper(id, setValue, subscriptionId);
                } else {
                    await updateUserFlag(id, {
                        flagName: queryParams.name,
                        value: setValue,
                    });
                }
                this.setFetching({ fetching: false });

                await this.loadOne({ id, reFetch: true });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async updateFeatureFlag ({ queryParams, setValue, id }: { queryParams: FlagQuery; setValue: unknown; id: string }) {
            this.setFetching({ fetching: true });
            try {
                await updateUserFeatureFlag(id, {
                    flagName: queryParams.name,
                    value: setValue,
                });
                this.setFetching({ fetching: false });

                await this.loadOne({ id, reFetch: true });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async updateFlags ({ user, flags }: { user: UserRecord; flags: Record<string, boolean> }) {
            this.setFetching({ fetching: true });

            try {
                const { falseRequests, trueRequests, adminChangeRequest } = Object.entries(flags).reduce(
                    (acc, flagPair) => {
                        const [flagName, flagValue] = flagPair;
                        if (flagName === 'systemUser') return acc;
                        if (flagName === 'admin') {
                            acc.adminChangeRequest = {
                                flagName,
                                value: flagValue,
                            };

                            return acc;
                        }

                        if (flagValue === true) {
                            acc.trueRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        if (flagValue === false) {
                            acc.falseRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        return acc;
                    },
                    {
                        adminChangeRequest: null as FlagRequest | null,
                        falseRequests: [] as FlagRequest[],
                        trueRequests: [] as FlagRequest[],
                    },
                );

                // If we're going to admin, do it first so certain flag combinations are allowed
                if (adminChangeRequest && adminChangeRequest.value === true) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
                await Promise.all(falseRequests.map((falseRequestPair) => updateUserFlag(user.id, falseRequestPair)));
                await Promise.all(trueRequests.map((trueRequestsPair) => updateUserFlag(user.id, trueRequestsPair)));
                // If we're going from admin, do it last after we removed disallowed certain flag combinations
                if (adminChangeRequest && adminChangeRequest.value === false) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }

            await this.loadOne({ id: user.id, reFetch: true });
        },
        async updateVendorUserForUser ({ userId, vendorUserPermissionId, first, last }: { userId: string; vendorUserPermissionId: string; first: string; last: string }) {
            this.setFetching({ fetching: true });

            try {
                await updateFeedbackVendorUserForUser({
                    userId,
                    vendorUserPermissionId,
                    first,
                    last,
                });
                await this.loadOne({ id: userId, reFetch: true });
                this.setFetching({ fetching: false });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useUsersStore, module.hot));
}
